/**
 * OUTDOOR GRILLS — the 3D bodies (2026-10-08, Matt: "a few different high-end barbecue models
 * under the Site tab that are a good quality like the light fixtures"). One builder per style in
 * the kernel catalog (plumbingStyles.ts `GRILL` — the grill rides the plumbing-styles machinery:
 * a per-instance Style, real sizes, a brushable metal). Inches, the object frame: x across
 * centred on 0, y from the back (0) toward the cook's side, z up. Built-in heads build with the
 * COUNTER at z = R (objectSolid drops them by R onto a real counter, like a sink); everything
 * else stands on the floor at z = 0.
 *
 * Roles: 'metal' (the brushable stainless / blackened steel), 'grate' (cast iron), 'knob',
 * 'char' (a firebox's burnt inside), 'wood' (teak), 'rubber' (tyres), 'body' (a kamado's
 * ceramic — its Body Finish), 'dark' (seams, dials).
 */
import { LB, add, type V3 } from '../../meshKit'

type YZ = [number, number]

/** a y–z profile (CCW, y to the front, z up) extruded across x0..x1, capped */
function extrudeX(L: LB, prof: YZ[], x0: number, x1: number) {
  const n = prof.length
  const A = prof.map(([y, z]) => [x0, y, z] as V3), B = prof.map(([y, z]) => [x1, y, z] as V3)
  for (let i = 0; i < n; i++) { const j = (i + 1) % n; L.quad(A[i], A[j], B[j], B[i]) }
  const cy = prof.reduce((s, p) => s + p[0], 0) / n, cz = prof.reduce((s, p) => s + p[1], 0) / n
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n
    L.tri([x1, cy, cz], B[i], B[j])
    L.tri([x0, cy, cz], A[j], A[i])
  }
}
/** cubic Bézier in the y–z plane */
function bezYZ(p0: YZ, p1: YZ, p2: YZ, p3: YZ, n = 10): YZ[] {
  const out: YZ[] = []
  for (let i = 0; i <= n; i++) {
    const t = i / n, s = 1 - t
    out.push([
      s * s * s * p0[0] + 3 * s * s * t * p1[0] + 3 * s * t * t * p2[0] + t * t * t * p3[0],
      s * s * s * p0[1] + 3 * s * s * t * p1[1] + 3 * s * t * t * p2[1] + t * t * t * p3[1],
    ])
  }
  return out
}
/** an open-topped box (floor + four walls) of wall thickness t */
function openBox(L: LB, x0: number, x1: number, y0: number, y1: number, z0: number, z1: number, t = 0.3) {
  L.box(x0, x1, y0, y1, z0, z0 + t)
  L.box(x0, x1, y0, y0 + t, z0, z1); L.box(x0, x1, y1 - t, y1, z0, z1)
  L.box(x0, x0 + t, y0, y1, z0, z1); L.box(x1 - t, x1, y0, y1, z0, z1)
}
/** a front knob (axis +y) with its bezel */
function knob(L: LB, x: number, y: number, z: number, r = 1.05) {
  L.role('dark').cyl([x, y, z], [x, y + 0.15, z], r * 1.35, 20)
  L.role('metal').cyl([x, y + 0.1, z], [x, y + 1.3, z], r, 18)
  L.role('metal').box(x - 0.18, x + 0.18, y + 1.25, y + 1.6, z - r * 0.8, z + r * 0.8)   // the grip
}
/** a spoked flywheel (axis along `ax`) */
function flywheel(L: LB, c: V3, r: number, ax: 'x' | 'y', spokes = 6, rim = 0.42) {
  L.role('metal').ring(c, r, rim, ax, 36, 10)
  for (let k = 0; k < spokes; k++) {
    const a = (Math.PI * 2 * k) / spokes, u = Math.cos(a) * r, v = Math.sin(a) * r
    const tip: V3 = ax === 'y' ? [c[0] + u, c[1], c[2] + v] : [c[0], c[1] + u, c[2] + v]
    L.tube([c, tip], 0.22, 8, false)
  }
  L.cyl(ax === 'y' ? add(c, [0, -0.6, 0]) : add(c, [-0.6, 0, 0]), ax === 'y' ? add(c, [0, 0.6, 0]) : add(c, [0.6, 0, 0]), 0.9, 16)
  // the turning handle on the rim
  const h: V3 = ax === 'y' ? [c[0] + r, c[1], c[2]] : [c[0], c[1] + r, c[2]]
  L.role('wood').cyl(h, ax === 'y' ? add(h, [0, 3, 0]) : add(h, [3, 0, 0]), 0.45, 12)
}
/** a caster / cart wheel (axis along y) at hub c */
function wheel(L: LB, c: V3, r: number, w = 2) {
  L.role('rubber').ring(c, r - 0.8, 0.8, 'y', 32, 10)
  L.role('metal').cyl(add(c, [0, -w / 2, 0]), add(c, [0, w / 2, 0]), r * 0.55, 20)
}

// ======================================================================================

/** the grill head: a firebox body, the sloped control panel with its knobs, the rounded hood,
 *  its handle and thermometer — from z0 (the head's bottom) up; d = head depth */
function grillHead(L: LB, W: number, d: number, z0: number, opts: { lowZ?: number; knobs?: number } = {}) {
  const a = W / 2, zb = z0 + 9, zt = z0 + 22.5
  L.role('metal').box(-a, a, 0, d - 4, z0, zb)                          // firebox body
  // the control panel: sloped forward, the knobs set into it
  extrudeX(L, [[d - 4, z0], [d - 0.8, z0 + 0.8], [d, z0 + 7.6], [d - 4, zb]], -a, a)
  const n = opts.knobs ?? Math.max(3, Math.round(W / 10))
  for (let i = 0; i < n; i++) {
    const x = -a + (W * (i + 0.5)) / n
    // the knob face is on the slope — set it in a little frame tipped back
    L.frame([x, d - 0.4, z0 + 4.2], [1, 0, 0, 0, Math.cos(0.12), -Math.sin(0.12), 0, Math.sin(0.12), Math.cos(0.12)], () => knob(L, 0, 0, 0))
  }
  // the hood: a rolled profile across the full width
  const top = bezYZ([d - 4.5, zb + 3.2], [d - 4.5, zt + 1], [6, zt + 1.4], [1.2, zb + 7], 12)
  const prof: YZ[] = [[1, zb], [d - 4.2, zb], [d - 4.2, zb + 2.6], ...top.slice(0, -1), [1.2, zb + 7]]
  L.role('metal')
  extrudeX(L, prof, -a + 0.4, a - 0.4)
  // the hood handle on its two stand-offs
  const hy = d - 1.4, hz = zb + 4.4
  for (const x of [-a + 3, a - 3]) L.tube([[x, d - 4.4, hz - 0.3], [x, hy, hz]], 0.35, 10)
  L.tube([[-a + 3, hy, hz], [a - 3, hy, hz]], 0.55, 14)
  // the hood thermometer
  L.role('metal').cyl([0, d - 5.2, zb + 8.6], [0, d - 4.2, zb + 7.6], 1.5, 20)
  L.role('dark').cyl([0, d - 4.25, zb + 7.65], [0, d - 4.05, zb + 7.45], 1.2, 20)
}

/** BUILT-IN GAS HEAD — drops into a counter cut-out (the counter at z = R) */
export function grillBuiltInGas(L: LB, W: number, R: number) {
  const a = W / 2
  L.role('metal').box(-a + 1.5, a - 1.5, 1, 22, R - 10.5, R)          // the liner below the counter
  L.box(-a, a, 0, 22.5, R, R + 0.35)                                  // the trim flange on the counter
  grillHead(L, W, 26, R + 0.35)
}

/** FREESTANDING HYBRID ON CART — a 36" head on a stainless cabinet with side shelves */
export function grillHybridCart(L: LB, W: number) {
  const hw = 18, sh = (W - 36) / 2
  // the cabinet on casters, its two doors and their pulls
  L.role('metal').box(-hw, hw, 2, 28, 5, 33)
  // two stainless doors standing just proud, a fine dark reveal round each
  L.role('dark').box(-hw + 0.8, hw - 0.8, 27.95, 28.02, 6.8, 31.2)
  for (const s of [-1, 1]) {
    L.role('metal').box(s > 0 ? 0.15 : -hw + 1, s > 0 ? hw - 1 : -0.15, 28, 28.3, 7, 31)
    L.role('metal').tube([[s * 2, 28.1, 26], [s * 2, 29.2, 26], [s * 2, 29.2, 12], [s * 2, 28.1, 12]], 0.3, 10, false)
  }
  for (const x of [-hw + 2.5, hw - 2.5]) for (const y of [5, 25]) {
    L.role('metal').cyl([x, y, 5], [x, y, 3.6], 0.9, 12)
    wheel(L, [x, y, 2.2], 2.2, 1.4)
  }
  grillHead(L, 36, 30, 33, { knobs: 4 })
  // the side shelves on their brackets, a towel bar along each front
  for (const s of [-1, 1]) {
    const x0 = s > 0 ? hw : -hw - sh, x1 = s > 0 ? hw + sh : -hw
    L.role('metal').box(x0, x1, 2, 26, 34.5, 36)
    L.tube([[s * hw, 4, 30], [s * (hw + sh * 0.7), 4, 34.5]], 0.35, 8)
    L.tube([[s * hw, 24, 30], [s * (hw + sh * 0.7), 24, 34.5]], 0.35, 8)
    const xa = s * (hw + 1.5), xb = s * (hw + sh - 1.5)
    L.tube([[xa, 26, 35], [xa, 27.6, 35], [xb, 27.6, 35], [xb, 26, 35]], 0.3, 10, false)
  }
}

/** CERAMIC KAMADO IN A TEAK TABLE */
export function grillKamadoTable(L: LB, W: number) {
  const a = W / 2, d = 30, zt = 30.5, cy = 15
  // the table: top, legs, lower shelf
  L.role('wood').box(-a, a, 0, d, zt - 1.5, zt)
  L.role('wood').box(-a + 2, a - 2, 3, d - 3, 6, 7)
  for (const x of [-a + 1.5, a - 1.5]) for (const y of [1.5, d - 1.5]) L.box(x - 1.25, x + 1.25, y - 1.25, y + 1.25, 0, zt - 1.5)
  // the egg: base, two band clamps, the dome, its vent cap, the handle and the draft door
  const z0 = 17.5
  L.role('body').latheAt([0, cy, z0], [[0, 16.5], [10.6, 16.5], [10.7, 14], [10.2, 9], [8.6, 4], [6.4, 1.2], [5.2, 0], [0, 0]], 40)
  L.latheAt([0, cy, z0 + 16.5], [[0, 15.2], [3.0, 15.2], [6.2, 13.6], [8.8, 10.6], [10.3, 6.4], [10.7, 2], [10.6, 0], [0, 0]], 40)
  L.role('metal').ring([0, cy, z0 + 15.8], 10.85, 0.42, 'z', 40, 8)
  L.ring([0, cy, z0 + 17.2], 10.85, 0.42, 'z', 40, 8)
  L.latheAt([0, cy, z0 + 31.5], [[0, 2.6], [1.6, 2.6], [2.6, 1.6], [2.8, 0], [0, 0]], 20)       // the vent cap
  L.role('metal').box(-2, 2, cy - 11.6, cy - 10.4, z0 + 13, z0 + 20)                            // the hinge
  for (const x of [-3.5, 3.5]) L.role('metal').tube([[x, cy + 9.8, z0 + 22], [x, cy + 12.2, z0 + 22.5]], 0.3, 8)
  L.role('wood').tube([[-4.2, cy + 12.4, z0 + 22.6], [4.2, cy + 12.4, z0 + 22.6]], 0.6, 12)
  L.role('metal').box(-2.6, 2.6, cy + 9.6, cy + 10.7, z0 + 4, z0 + 8)                          // draft door
}

/** ARGENTINE WOOD-FIRE GRILL, BUILT IN — the brasero, two V-grates on cranks */
export function grillArgentine(L: LB, W: number, R: number) {
  const a = W / 2
  L.role('char')
  openBox(L, -a + 1, a - 1, 2, 24, R - 8, R, 0.3)                       // the firebox, below the counter
  L.role('metal').box(-a, a, 0, 25, R, R + 0.3)                          // the flange
  // the cheeks and the tall back
  for (const s of [-1, 1]) L.plateY(s < 0 ? [[-a, R], [-a + 0.5, R], [-a + 0.5, R + 24], [-a, R + 24]] : [[a - 0.5, R], [a, R], [a, R + 24], [a - 0.5, R + 24]], 0, 25)
  L.box(-a, a, 0, 0.6, R, R + 24)
  // the brasero: a fire basket of bars across the back
  L.role('grate')
  const bx0 = -a + 3, bx1 = a - 3
  for (let x = bx0; x <= bx1 + 1e-6; x += 1.6) for (const y of [3, 9]) L.cyl([x, y, R + 2], [x, y, R + 16], 0.22, 6)
  for (const z of [R + 2, R + 9, R + 16]) for (const y of [3, 9]) L.cyl([bx0, y, z], [bx1, y, z], 0.3, 6)
  L.box(bx0, bx1, 3, 9, R + 1.6, R + 2)
  // two V-grates side by side, each on its own crank
  const gw = (W - 3) / 2
  for (const s of [-1, 1]) {
    const gx0 = s < 0 ? -a + 1.2 : 0.3, gx1 = gx0 + gw - 1.5, gz = R + 7
    L.role('grate').box(gx0, gx1, 10, 10.6, gz - 0.6, gz + 0.6)
    L.box(gx0, gx1, 23.4, 24, gz - 0.6, gz + 0.6)
    // the V-channel rods that carry the drippings forward
    for (let x = gx0 + 0.7; x < gx1 - 0.3; x += 1.1) {
      L.cyl([x - 0.3, 10.6, gz + 0.25], [x - 0.3, 23.4, gz + 0.1], 0.14, 5)
      L.cyl([x + 0.3, 10.6, gz + 0.25], [x + 0.3, 23.4, gz + 0.1], 0.14, 5)
      L.cyl([x, 10.6, gz - 0.2], [x, 23.4, gz - 0.35], 0.16, 5)
    }
    const cx = (gx0 + gx1) / 2
    // the cables up to the axle, the flywheel out front
    L.role('metal').tube([[gx0 + 1, 11, gz + 0.6], [gx0 + 1, 11, R + 21]], 0.12, 6, false)
    L.tube([[gx1 - 1, 11, gz + 0.6], [gx1 - 1, 11, R + 21]], 0.12, 6, false)
    flywheel(L, [cx, 26.5, R + 18], 4.8, 'y', 6, 0.38)
    L.role('metal').cyl([cx, 0.6, R + 18], [cx, 26.5, R + 18], 0.45, 10)
  }
  L.role('metal').cyl([-a, 11, R + 21], [a, 11, R + 21], 0.55, 12)      // the top axle
}

/** SANTA MARIA ON A CART — an open steel tray, the grate on a gallows and a flywheel */
export function grillSantaMaria(L: LB, W: number) {
  const a = 24, d = 30, zt = 33
  // the cart: legs, lower shelf, two wheels at one end
  L.role('metal')
  for (const x of [-a + 1, a - 1]) for (const y of [2, d - 2]) L.box(x - 0.75, x + 0.75, y - 0.75, y + 0.75, x < 0 ? 6 : 0, zt - 9)
  L.box(-a + 1, a - 1, 2, d - 2, 10, 10.6)
  for (const y of [-0.5, d + 0.5]) wheel(L, [-a + 1, y, 6], 6, 1.8)
  L.cyl([-a + 1, -1.4, 6], [-a + 1, d + 1.4, 6], 0.5, 10)
  // the firebox tray
  L.role('char')
  openBox(L, -a, a, 0, d, zt - 9, zt, 0.3)
  // the grate, raised to mid height
  L.role('grate').box(-a + 1, a - 1, 1, 1.6, zt - 3.6, zt - 2.6)
  L.box(-a + 1, a - 1, d - 1.6, d - 1, zt - 3.6, zt - 2.6)
  for (let x = -a + 1.6; x < a - 1; x += 1) L.cyl([x, 1.6, zt - 3.1], [x, d - 1.6, zt - 3.1], 0.22, 6)
  // the gallows: two posts, the crossbar, the cables, the flywheel on the right
  L.role('metal')
  for (const x of [-a - 0.6, a + 0.6]) L.box(x - 0.6, x + 0.6, d / 2 - 0.6, d / 2 + 0.6, zt - 9, zt + 25)
  L.cyl([-a - 0.6, d / 2, zt + 24], [a + 2.5, d / 2, zt + 24], 0.6, 12)
  for (const x of [-a + 3, a - 3]) for (const y of [2, d - 2]) L.tube([[x, y, zt - 2.6], [x, d / 2, zt + 23.4]], 0.1, 5, false)
  flywheel(L, [a + 2.5, d / 2, zt + 18], 7, 'x', 6, 0.45)
  L.cyl([a + 0.6, d / 2, zt + 18], [a + 2.5, d / 2, zt + 18], 0.5, 10)
}

/** OFFSET WOOD SMOKER — the barrel cook chamber, the firebox, the stack, on a wheeled frame */
export function grillOffsetSmoker(L: LB, W: number) {
  const cy = 15, cz = 37, r = 12, x0 = -30, x1 = 13
  L.role('metal')
  L.frame([x0, cy, cz], [0, 0, 1, 0, 1, 0, -1, 0, 0], () => {
    // a lathe along x: the barrel with its two dished ends
    const len = x1 - x0
    L.lathe([[0, len], [r - 1, len], [r, len - 0.8], [r, 0.8], [r - 1, 0], [0, 0]], 40)
  })
  // the lid seam, the lid handle, the thermometer
  L.role('dark').tube(Array.from({ length: 17 }, (_, i) => { const t = (-0.15 + (i / 16) * 1.3) * Math.PI; return [x0 + 1.5, cy + Math.cos(t) * (r + 0.05), cz + Math.sin(t) * (r + 0.05)] as V3 }), 0.08, 5, false)
  L.tube(Array.from({ length: 17 }, (_, i) => { const t = (-0.15 + (i / 16) * 1.3) * Math.PI; return [x1 - 1.5, cy + Math.cos(t) * (r + 0.05), cz + Math.sin(t) * (r + 0.05)] as V3 }), 0.08, 5, false)
  for (const x of [x0 + 6, x1 - 6]) L.role('metal').tube([[x, cy + r - 0.2, cz + 3], [x, cy + r + 2.2, cz + 3]], 0.3, 8)
  L.role('wood').cyl([x0 + 5, cy + r + 2.4, cz + 3], [x1 - 5, cy + r + 2.4, cz + 3], 0.7, 12)
  L.role('metal').cyl([x0 + 4, cy + r * 0.7, cz + r * 0.7], [x0 + 4, cy + r * 0.78, cz + r * 0.78], 1.6, 20)
  // the firebox: a heavy cube with its door and vent spinner
  L.role('metal').box(x1, x1 + 19, cy - 10, cy + 10, cz - 13, cz + 6)
  L.role('dark').box(x1 + 2.5, x1 + 16.5, cy + 10, cy + 10.1, cz - 11, cz + 3)
  L.role('metal').cyl([x1 + 19, cy, cz - 6], [x1 + 19.6, cy, cz - 6], 2.6, 20)
  L.role('wood').cyl([x1 + 6, cy + 10.2, cz + 1], [x1 + 13, cy + 10.2, cz + 1], 0.6, 10)
  // the stack, at the far end
  L.role('metal').cyl([x0 + 4, cy, cz + r - 1], [x0 + 4, cy, cz + 37], 3, 20)
  L.cyl([x0 + 4, cy, cz + 37], [x0 + 4, cy, cz + 37.6], 3.6, 20)
  // the frame: four legs, a shelf, the wheels under the firebox end
  for (const x of [x0 + 3, x1 + 16]) for (const y of [cy - 10, cy + 10]) L.box(x - 0.9, x + 0.9, y - 0.9, y + 0.9, x > 0 ? 11 : 0, cz - (x > 0 ? 13 : r) + 0.5)
  L.role('wood').box(x0 + 3, x1 + 16, cy - 9, cy + 9, 9, 10)
  for (const y of [cy - 12, cy + 12]) wheel(L, [x1 + 10, y, 9], 9, 2.4)
  L.role('metal').cyl([x1 + 10, cy - 13, 9], [x1 + 10, cy + 13, 9], 0.6, 10)
}
